// Header.tsx
import { useMemo, useEffect, useState } from 'react';
import { Box, Typography, Avatar, Tooltip, IconButton, useTheme } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useAuth, resolveAvatar } from '../../../../context/AuthContext';

type HeaderProps = { sidebarOpen: boolean; onToggleSidebar: () => void };

export default function Header({ sidebarOpen, onToggleSidebar }: HeaderProps) {
  const { user, bust } = useAuth();
  const theme = useTheme();

  const [elevated, setElevated] = useState(false);
  useEffect(() => {
    const scroller = document.getElementById('app-scroll');
    if (!scroller) return;
    const onScroll = () => setElevated(scroller.scrollTop > 2);
    onScroll();
    scroller.addEventListener('scroll', onScroll, { passive: true });
    return () => scroller.removeEventListener('scroll', onScroll);
  }, []);

  const fallbackFromEmail = useMemo(() => {
    const email = user?.email || '';
    const base = email.split('@')[0] || '';
    if (!base) return 'Felhasználó';
    return base
      .split(/[._-]+/g)
      .filter(Boolean)
      .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
      .join(' ');
  }, [user?.email]);

  const displayName = user?.displayName?.trim() ? user!.displayName! : fallbackFromEmail;

  const initials = useMemo(
    () =>
      (displayName || 'F')
        .split(' ')
        .map((p) => p[0])
        .join('')
        .slice(0, 2)
        .toUpperCase(),
    [displayName]
  );

  const avatarSrc = useMemo(() => resolveAvatar(user?.profileImageUrl, bust), [user?.profileImageUrl, bust]);

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        // ↓ A header legyen a backdrop alatt mobilon
        zIndex: 1300,
        backdropFilter: 'blur(6px)',
        bgcolor: theme.palette.mode === 'dark' ? 'rgba(18,18,18,0.6)' : 'rgba(255,255,255,0.7)',
        borderBottom: 1,
        borderColor: 'divider',
        px: { xs: 2, md: 3 },
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        boxShadow: elevated ? 2 : 'none',
        transition: 'box-shadow 0.2s ease',
      }}
    >
      {/* bal oldal */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <IconButton
          aria-label={sidebarOpen ? 'Oldalsáv bezárása' : 'Oldalsáv megnyitása'}
          onClick={onToggleSidebar}
          edge="start"
        >
          {sidebarOpen ? <MenuOpenIcon /> : <MenuIcon />}
        </IconButton>

        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            sx={{ fontWeight: 700, letterSpacing: 0.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            Üdv, {displayName}!
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Jó elemzést! ✨
          </Typography>
        </Box>
      </Box>

      {/* jobb oldal */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
        <Tooltip title={user?.email || ''}>
          <Typography
            variant="body2"
            sx={{
              maxWidth: { xs: 160, sm: 260 },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 500,
              color: 'text.secondary',
            }}
          >
            {user?.email}
          </Typography>
        </Tooltip>

        <Avatar
          alt={displayName}
          src={avatarSrc}
          sx={{
            width: 36,
            height: 36,
            fontSize: 14,
            bgcolor: theme.palette.mode === 'dark' ? 'primary.dark' : 'primary.light',
            border: '2px solid',
            borderColor: theme.palette.mode === 'dark' ? 'grey.800' : 'grey.200',
          }}
        >
          {initials}
        </Avatar>
      </Box>
    </Box>
  );
}
